/**
 * usePdfReadConfirmation — React hook to unlock a CTA button once the
 * customer has demonstrably "read" an embedded PDF document.
 *
 * Designed per A. Zorzutti / LoJack Legal communication 05/06/2026 +
 * Fabrizio req. 11/06/2026 (scroll-detection reliability):
 *
 *   "Teniamo lo sniffer sullo scroll down dell'utente nella versione
 *    desktop, mentre facciamo il downgrade al countdown SOLO nella
 *    versione mobile per evitare possibili malfunzionamenti."
 *
 * Strategy:
 *   - DESKTOP: listen to the scroll events emitted by EITHER
 *       (a) an HTML container ref (`scrollContainerRef`) → preferred,
 *           because browsers RELIABLY emit scroll events on real HTML
 *           elements. Used together with <ScrollablePdfReader> which
 *           renders the PDF via react-pdf inside a scrollable div.
 *       (b) the legacy iframe ref (`iframeRef`) → kept as fallback.
 *     When the scrollTop + clientHeight reaches the bottom of
 *     scrollHeight (within SCROLL_TOLERANCE_PX), the CTA becomes
 *     available. Works on Chrome, Firefox, Edge, Brave on
 *     macOS/Windows/Linux desktop.
 *   - MOBILE: fall back to a countdown timer of `minSeconds`. iOS
 *     Safari and Android Chrome's native PDF viewers DO NOT expose
 *     scroll events on the iframe (the PDF is rendered by a separate
 *     viewer process), so any scroll-end detector would never fire
 *     and the user would be blocked forever.
 *
 * Detection of "mobile" combines THREE checks (any-of):
 *   1. matchMedia('(max-width: 768px)') — narrow viewport
 *   2. 'ontouchstart' in window — touch capability
 *   3. iPad/iPhone/iPod in userAgent — iPadOS lies about width in
 *      landscape (>768px), but its Safari is the worst-case offender.
 *
 * The hook returns the `{readDone, secondsRemaining, scrolledToEnd,
 * mode}` quadruple so the consuming component can render either a
 * scroll-progress hint OR a countdown bar.
 */
import { useCallback, useEffect, useRef, useState } from "react";

const detectMobile = () => {
  if (typeof window === "undefined") return false;
  try {
    if (window.matchMedia && window.matchMedia("(max-width: 768px)").matches) return true;
    if ("ontouchstart" in window) return true;
    if (/iPad|iPhone|iPod/i.test(navigator.userAgent || "")) return true;
  } catch (_err) { /* SSR safety */ }
  return false;
};

const SCROLL_TOLERANCE_PX = 24;          // 1.5 lines @ ~16px font
// Safety unlock — only triggers when the iframe-based legacy path is in
// use AND something prevents scroll detection (cross-origin, plugin viewer).
// When `scrollContainerRef` is provided (react-pdf path), the HTML scroll
// is guaranteed to fire, so we disable the safety entirely to enforce
// "must scroll" (Fabrizio 11/06/2026).
const LEGACY_FALLBACK_TIMEOUT_SECS = 30;

export const usePdfReadConfirmation = ({
  iframeRef,
  scrollContainerRef,
  minSeconds = 15,
  enabled = true,
} = {}) => {
  const [mode, setMode] = useState(() => (detectMobile() ? "countdown" : "scroll"));
  const [scrolledToEnd, setScrolledToEnd] = useState(false);
  const [secondsElapsed, setSecondsElapsed] = useState(0);
  const [progressFraction, setProgressFraction] = useState(0); // 0..1 scroll progress (desktop)
  const tickerRef = useRef(null);
  const fallbackRef = useRef(null);

  // Re-evaluate on window resize (rotate iPad / responsive devtools)
  useEffect(() => {
    const onResize = () => setMode(detectMobile() ? "countdown" : "scroll");
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Mobile: countdown timer
  useEffect(() => {
    if (!enabled || mode !== "countdown") return undefined;
    setSecondsElapsed(0);
    tickerRef.current = setInterval(() => {
      setSecondsElapsed((prev) => {
        if (prev >= minSeconds) { clearInterval(tickerRef.current); return minSeconds; }
        return prev + 1;
      });
    }, 1000);
    return () => clearInterval(tickerRef.current);
  }, [mode, minSeconds, enabled]);

  // Desktop: scroll listener.
  // Preferred path: scroll on the HTML container (react-pdf wrapper). This
  // is the ONLY reliable mode and has no safety-timeout — we require the
  // customer to genuinely scroll.
  // Fallback path: scroll on iframe contentDocument with a safety timeout
  // (kept for backward-compat with legacy callers still using <iframe>).
  useEffect(() => {
    if (!enabled || mode !== "scroll") return undefined;
    setScrolledToEnd(false);
    setProgressFraction(0);

    // ─── Preferred path: HTML container ─────────────────────────────────
    if (scrollContainerRef?.current) {
      const el = scrollContainerRef.current;
      const checkScroll = () => {
        // Guard: ignore the initial check until the PDF has rendered enough
        // content to make the container actually scrollable. Without this,
        // a freshly-mounted reader (scrollHeight == clientHeight while
        // react-pdf is still loading) would immediately satisfy the
        // "distanceFromBottom <= tolerance" condition and unlock the
        // checkbox before the customer scrolled a single pixel.
        const isActuallyScrollable = el.scrollHeight > el.clientHeight + SCROLL_TOLERANCE_PX;
        if (!isActuallyScrollable) return;
        const distanceFromBottom = el.scrollHeight - (el.scrollTop + el.clientHeight);
        const denom = Math.max(1, el.scrollHeight - el.clientHeight);
        const frac = Math.min(1, Math.max(0, el.scrollTop / denom));
        setProgressFraction(frac);
        if (distanceFromBottom <= SCROLL_TOLERANCE_PX) {
          setScrolledToEnd(true);
        }
      };
      el.addEventListener("scroll", checkScroll, { passive: true });
      // Re-evaluate periodically while react-pdf is still rendering pages —
      // scrollHeight grows as each page mounts, and we want progressPct to
      // start showing meaningful values once the document is scrollable.
      const pollId = setInterval(checkScroll, 500);
      checkScroll();
      return () => {
        el.removeEventListener("scroll", checkScroll);
        clearInterval(pollId);
      };
    }

    // ─── Fallback path: iframe (legacy callers) ─────────────────────────
    const iframe = iframeRef?.current;
    if (!iframe) return undefined;

    const checkScroll = () => {
      try {
        const doc = iframe.contentDocument || iframe.contentWindow?.document;
        if (!doc) return;
        const target = doc.documentElement.scrollHeight > doc.documentElement.clientHeight
          ? doc.documentElement
          : doc.body;
        if (!target) return;
        const distanceFromBottom = target.scrollHeight - (target.scrollTop + target.clientHeight);
        const denom = Math.max(1, target.scrollHeight - target.clientHeight);
        setProgressFraction(Math.min(1, Math.max(0, target.scrollTop / denom)));
        if (distanceFromBottom <= SCROLL_TOLERANCE_PX) {
          setScrolledToEnd(true);
        }
      } catch (_err) {
        // Cross-origin iframe blocks contentDocument access — safety
        // fallback below will eventually unlock the CTA.
      }
    };

    const attach = () => {
      try {
        const doc = iframe.contentDocument || iframe.contentWindow?.document;
        if (doc) {
          doc.addEventListener("scroll", checkScroll, { passive: true });
          checkScroll();
        }
      } catch (_err) { /* same-origin only */ }
    };

    iframe.addEventListener("load", attach);
    if (iframe.contentDocument?.readyState === "complete") attach();

    // Safety timeout ONLY for the iframe path — see note at top of file.
    fallbackRef.current = setTimeout(() => setScrolledToEnd(true),
                                     LEGACY_FALLBACK_TIMEOUT_SECS * 1000);

    return () => {
      iframe.removeEventListener("load", attach);
      try {
        const doc = iframe.contentDocument || iframe.contentWindow?.document;
        if (doc) doc.removeEventListener("scroll", checkScroll);
      } catch (_err) { /* same-origin only */ }
      clearTimeout(fallbackRef.current);
    };
  }, [iframeRef, scrollContainerRef, mode, enabled]);

  // Allow consumer to manually force-unlock (e.g. "Open in new tab"
  // button fired by the user — they took explicit action)
  const forceComplete = useCallback(() => {
    if (mode === "scroll") setScrolledToEnd(true);
    else setSecondsElapsed(minSeconds);
  }, [mode, minSeconds]);

  const readDone = mode === "scroll" ? scrolledToEnd : secondsElapsed >= minSeconds;
  const secondsRemaining = mode === "countdown"
    ? Math.max(0, minSeconds - secondsElapsed)
    : 0;
  const progressPct = mode === "scroll"
    ? (scrolledToEnd ? 100 : Math.round(progressFraction * 100))
    : Math.min(100, (secondsElapsed / minSeconds) * 100);

  return {
    mode,                  // "scroll" | "countdown"
    readDone,              // true once unlock condition met
    scrolledToEnd,         // desktop only
    secondsElapsed,        // mobile only (and exposed for debugging)
    secondsRemaining,      // mobile only
    progressPct,           // 0..100 for UI progress bar
    forceComplete,         // manual unlock helper
  };
};
